import SEO from "@/components/SEO";
import FAQAccordion from "@/components/FAQAccordion";
import { RelatedLinks } from "@/pages/ServicePage";
import { PageHero, SectionHead, CTABand, Reveal } from "@/components/Ui";
import { CheckCircle2 } from "lucide-react";

const prestations = [
  "Rénovation de maçonnerie ancienne et de façades",
  "Réparation de murs et traitement des fissures",
  "Rejointoiement brique, pierre et silex",
  "Ouvertures : portes, fenêtres, baies vitrées",
  "Reprises de maçonnerie et renforts localisés",
  "Rénovation intérieure et extérieure",
  "Transformation de bâtiments et d'espaces",
  "Travaux liés aux extensions et ouvertures",
];

const faq = [
  { q: "Quels types de rénovation réalisez-vous ?", a: "Réparation de murs, traitement des fissures, rejointoiement, reprises de maçonnerie et rénovation intérieure et extérieure, notamment sur le bâti ancien." },
  { q: "Rénovez-vous les maisons anciennes ?", a: "Oui : longères et bâtis en brique, silex ou colombages. Nous rénovons en respectant les matériaux d'origine." },
  { q: "Comment se déroule une rénovation avec DMZ ?", a: "Nous commençons par un diagnostic sur place pour identifier les causes des désordres, puis nous proposons la réparation adaptée avec un devis gratuit." },
];

export default function RenovationBernay() {
  return (
    <>
      <SEO
        title="Rénovation de maison à Bernay & dans l'Eure | DMZ Normandie"
        description="Entreprise de rénovation à Bernay : réparation de murs, fissures, rejointoiement, rénovation de maçonnerie en Normandie. Devis gratuit — 07 45 04 91 89."
        path="/renovation-bernay"
      />
      <PageHero
        title="Rénovation de maison à Bernay"
        subtitle="Un mur fissuré, une façade à rénover, une maison ancienne à transformer ? DMZ Normandie redonne solidité et cachet à votre bien, à Bernay, Évreux et dans toute l'Eure."
      />

      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-14">
          <Reveal delay={0.05} className="order-2 lg:order-1 space-y-6">
            <div className="overflow-hidden relative">
              <img src="/realisations/facade-brique-silex.webp" alt="Rénovation de maçonnerie à Bernay : façade brique et silex rénovée par DMZ Normandie" className="w-full h-80 object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-black/25" />
            </div>
            <div className="card-dark p-8">
              <h2 className="font-serif-display font-light text-2xl md:text-3xl text-[#F5F5F0] mb-4">Rénover sans dénaturer</h2>
              <p className="text-sm font-light text-zinc-400 leading-relaxed">
                Les maisons de la région de Bernay ont souvent du caractère : brique, silex, colombages.
                Nous rénovons en respectant les matériaux d'origine et en renforçant ce qui doit l'être,
                pour un résultat durable et harmonieux.
              </p>
              <p className="mt-4 text-sm font-light text-zinc-400 leading-relaxed">
                Notre périmètre : la rénovation de maçonnerie à Bernay, Évreux et dans l'Eure —
                réparation de murs, fissures, rejointoiement, reprises et transformations. Avant de
                chiffrer, nous venons constater l'état réel de votre bien : pas de devis à distance
                approximatif, mais une proposition concrète, adaptée à votre maison et à votre budget.
              </p>
            </div>
          </Reveal>
          <Reveal className="order-1 lg:order-2">
            <SectionHead overline="Nos prestations" title="La rénovation, de la fissure à la façade" />
            <div className="space-y-4 -mt-6">
              {prestations.map((p) => (
                <p key={p} className="flex items-start gap-3 text-zinc-300 font-light">
                  <CheckCircle2 size={18} className="text-[#C4A484] mt-0.5 shrink-0" /> {p}
                </p>
              ))}
            </div>
            <p className="mt-8 text-sm font-light text-zinc-400 leading-relaxed border-l-2 border-[#C4A484] pl-5">
              Chaque rénovation commence par un diagnostic sur place : nous identifions les causes
              (humidité, tassement, vieillissement) avant de proposer la réparation adaptée.
            </p>
          </Reveal>
        </div>

        <div className="max-w-4xl mx-auto px-6 lg:px-10 mt-20">
          <SectionHead overline="Questions fréquentes" title="Vos questions, nos réponses" />
          <FAQAccordion items={faq} testid="faq-renovation" />
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-10 mt-16">
          <Reveal>
            <p className="text-[10px] tracking-[0.5em] uppercase text-[#C4A484] mb-5">Voir aussi</p>
            <RelatedLinks slugs={["maconnerie-bernay", "rejointoiement", "construction-murs", "extension-maison"]} />
          </Reveal>
        </div>
      </section>

      <CTABand />
    </>
  );
}
